// Project Save/Import utilities for .jsont format
import { graphToTwee } from './tweeConverter';

/**
 * Serialize the current project state to .jsont format
 * @param {Object} graph - The graph object with nodes, edges, storyTitle
 * @param {Object} settings - Editor settings (layout, zoom, formats, etc.)
 * @returns {string} JSON string ready for download
 */
export const serializeProject = (graph, settings = {}) => {
  const project = {
    version: '1.0',
    format: 'tweeporter-jsont',
    projectName: graph.storyTitle || 'Untitled Project',
    exportedAt: new Date().toISOString(),
    source: settings.source || 'manual',
    twee: graphToTwee(graph),
    graph: {
      nodes: graph.nodes.map(node => ({
        id: node.id,
        name: node.name,
        body: node.body || '',
        url: node.url || null,
        nodeLevel: node.nodeLevel || null,
        nodeStyle: node.nodeStyle || 'standard',
        displayName: node.displayName || node.name,
        isDirectUrl: node.isDirectUrl || false,
        chapterId: node.chapterId || null,
        intention: node.intention || null,
        purpose: node.purpose || null,
        dilemma: node.dilemma || null
      })),
      edges: graph.edges || [],
      storyTitle: graph.storyTitle,
      storyVision: graph.storyVision || '',
      storyChallenges: graph.storyChallenges || ''
    },
    settings: {
      // Basic settings
      nodeFormats: settings.nodeFormats || null,
      layoutDirection: settings.layoutDirection || 'right',
      showDescription: settings.showDescription || false,
      showBackLinks: settings.showBackLinks || false,
      hideH1Nodes: settings.hideH1Nodes || false,
      hideH2Nodes: settings.hideH2Nodes || false,
      focusLayerDepth: settings.focusLayerDepth || 2,
      dynamicViewSize: settings.dynamicViewSize || 'standard',
      
      // PathNav Sitemap settings
      pathNavSitemapEnabled: settings.pathNavSitemapEnabled || false,
      pathNavFocusColor: settings.pathNavFocusColor || '#667eea',
      pathNavStartMinimized: settings.pathNavStartMinimized || false,
      
      // Node Width Padding settings
      nodeWidthPaddingEnabled: settings.nodeWidthPaddingEnabled || false,
      nodeWidthPaddingPercent: settings.nodeWidthPaddingPercent || 100,
      
      // Focus View settings
      focusNodeViewEnabled: settings.focusNodeViewEnabled || false,
      focusTravelCenter: settings.focusTravelCenter || false,
      
      // Dynamic View settings
      dynamicViewEnabled: settings.dynamicViewEnabled || false,
      
      // Node alignment
      nodeAlignment: settings.nodeAlignment || 'center'
    },
    metadata: {
      passages: graph.nodes.length,
      connections: (graph.edges || []).length
    }
  };
  
  return JSON.stringify(project, null, 2);
};

/**
 * Download the project as a .jsont file
 * @param {Object} graph - The graph object
 * @param {Object} settings - Editor settings
 */
export const downloadProject = (graph, settings = {}) => {
  const json = serializeProject(graph, settings);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  
  const projectName = (graph.storyTitle || 'project')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  const date = new Date().toISOString().split('T')[0];
  const filename = `${projectName}-${date}.jsont`;
  
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  
  return filename;
};

/**
 * Parse and validate a .jsont file
 * @param {string} jsonString - Raw JSON string from file
 * @returns {Object} Parsed project data or throws error
 */
export const parseProjectFile = (jsonString) => {
  let data;
  try {
    data = JSON.parse(jsonString);
  } catch (e) {
    throw new Error('Invalid JSON format. Please select a valid .jsont file.');
  }
  
  // Log for debugging
  console.log('[ProjectSerializer] Parsing file, keys found:', Object.keys(data));
  
  // Check for legacy format (direct graph without wrapper)
  if (data.nodes && Array.isArray(data.nodes)) {
    console.log('[ProjectSerializer] Detected legacy/direct graph format');
    return {
      graph: {
        nodes: data.nodes.map(node => ({
          id: node.id,
          name: node.name,
          body: node.body || '',
          url: node.url || null,
          nodeLevel: node.nodeLevel || null,
          nodeStyle: node.nodeStyle || 'standard',
          displayName: node.displayName || node.name,
          isDirectUrl: node.isDirectUrl || false,
          chapterId: node.chapterId || null,
          intention: node.intention || null,
          purpose: node.purpose || null,
          dilemma: node.dilemma || null
        })),
        edges: data.edges || [],
        storyTitle: data.storyTitle || 'Imported Project',
        storyVision: data.storyVision || '',
        storyChallenges: data.storyChallenges || ''
      },
      settings: data.settings || data.editorConfig || {},
      metadata: {},
      twee: data.twee || '',
      projectName: data.storyTitle || 'Imported Project',
      source: 'import'
    };
  }
  
  // Validate required fields for standard format
  if (!data.graph || !data.graph.nodes) {
    console.error('[ProjectSerializer] Missing graph data. File contents:', JSON.stringify(data).substring(0, 500));
    throw new Error('Invalid project file: missing graph data. File may be corrupted or wrong format.');
  }
  
  if (!Array.isArray(data.graph.nodes)) {
    throw new Error('Invalid project file: nodes must be an array.');
  }
  
  return {
    graph: {
      nodes: data.graph.nodes.map(node => ({
        id: node.id,
        name: node.name,
        body: node.body || '',
        url: node.url || null,
        nodeLevel: node.nodeLevel || null,
        nodeStyle: node.nodeStyle || 'standard',
        displayName: node.displayName || node.name,
        isDirectUrl: node.isDirectUrl || false,
        chapterId: node.chapterId || null,
        intention: node.intention || null,
        purpose: node.purpose || null,
        dilemma: node.dilemma || null
      })),
      edges: data.graph.edges || [],
      storyTitle: data.graph.storyTitle || data.projectName || 'Imported Project',
      storyVision: data.graph.storyVision || '',
      storyChallenges: data.graph.storyChallenges || ''
    },
    settings: data.settings || {},
    metadata: data.metadata || {},
    twee: data.twee || '',
    projectName: data.projectName || 'Imported Project',
    source: data.source || 'import'
  };
};

/**
 * Import project from file input
 * @param {File} file - File object from input
 * @returns {Promise<Object>} Parsed project data
 */
export const importProjectFromFile = (file) => {
  return new Promise((resolve, reject) => {
    if (!file.name.endsWith('.jsont') && !file.name.endsWith('.json')) {
      reject(new Error('Please select a .jsont file.'));
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const result = parseProjectFile(e.target.result);
        resolve(result);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file.'));
    reader.readAsText(file);
  });
};
